import { HttpClient } from '@angular/common/http';
import { DataService } from '../../../core/service/base/data.service';
import { Observable, catchError, finalize, of, tap, map } from 'rxjs';
import { IAppConfig } from './../../../app.config';
import { RentalObject } from '../../model/rental-object';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { MessageService } from '../../../core/service/message.service';
import { Contract } from '../../model/contract';
import { FormOfLease } from '../../model/const/enums';
import { RentalObjectImprovement } from '../../model/rental-object-improvement';
import { LeaseRow } from '../../model/lease-row';
import { Injectable } from '@angular/core';

@Injectable()
export abstract class RentalObjectService<TModel extends RentalObject> extends DataService<TModel> {
  constructor(
    httpClient: HttpClient,
    config: IAppConfig,
    messageService: MessageService,
    modelConstructor: { new (): TModel },
    progressBar: ProgressBarService,
    serviceEndPoint: string
  ) {
    super(httpClient, config, messageService, modelConstructor, progressBar, serviceEndPoint);
    this.leaseAgreementRowEndpoint = `${config.apiEndpointV2}/lease-agreement-row-template`;
  }

  private leaseAgreementRowEndpoint;

  getValidFormsOfLease(companyId: string): Observable<FormOfLease[]> {
    return of([FormOfLease.Lease]);
  }

  protected createModel(data: any): TModel {
    const model = super.createModel(data);
    if (data.leaseAgreements instanceof Array) {
      model.leaseAgreements = data.leaseAgreements.map(item => Object.assign(new Contract(item.category), item));
    }

    if (data.improvements instanceof Array) {
      model.improvements = data.improvements.map(item => Object.assign(new RentalObjectImprovement(item.category), item));
    }

    return model;
  }

  getStatistics(): Observable<any> {
    return this.client.get(this.endpoint + '/statistics');
  }

  getCompanyStatistics(companyId: string): Observable<any> {
    if (companyId === undefined) companyId = '00000000-0000-0000-0000-000000000000';

    return this.client.get(`${this.endpoint}/companystatistics/${companyId}`);
  }

  getPropertyStatistics(propertyId: string): Observable<any> {
    if (propertyId === undefined) propertyId = '00000000-0000-0000-0000-000000000000';

    return this.client.get(`${this.endpoint}/propertystatistics/${propertyId}`);
  }

  getBuildingStatistics(buildingId: string): Observable<any> {
    if (buildingId === undefined) buildingId = '00000000-0000-0000-0000-000000000000';

    return this.client.get(`${this.endpoint}/buildingstatistics/${buildingId}`);
  }

  getVacant(): Observable<any> {
    throw new Error();
  }

  applyLeaseAgreementRow(id: string, rowModel: LeaseRow): Observable<LeaseRow[]> {
    const queryModel = {
      entityId: id,
      rowModel: rowModel,
    };

    this.progressBar.start();
    return this.client.put<LeaseRow[]>(`${this.leaseAgreementRowEndpoint}/apply-row`, JSON.stringify(queryModel)).pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      tap(() => this.notification.showSuccess('SUCCESS.LEASE_AGREEMENT_APPLY_ROW_TEMPLATE')),
      finalize(() => this.progressBar.stop())
    );
  }

  editLeaseAgreementRow(id: string, rowModel: LeaseRow): Observable<LeaseRow[]> {
    const queryModel = {
      entityId: id,
      rowModel: rowModel,
    };

    this.progressBar.start();
    return this.client.put<LeaseRow[]>(`${this.leaseAgreementRowEndpoint}/edit-row`, JSON.stringify(queryModel)).pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      tap(() => this.notification.showSuccess('SUCCESS.LEASE_AGREEMENT_APPLY_ROW_TEMPLATE')),
      finalize(() => this.progressBar.stop())
    );
  }

  removeLeaseAgreementRow(agreementId: string, rowId: string): Observable<LeaseRow[]> {
    const queryModel = {
      entityId: agreementId,
      rowId: rowId,
    };

    this.progressBar.start();
    return this.client.put<LeaseRow[]>(`${this.leaseAgreementRowEndpoint}/remove-row`, JSON.stringify(queryModel)).pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      tap(() => this.notification.showSuccess('SUCCESS.LEASE_AGREEMENT_REMOVE_ROW_TEMPLATE')),
      finalize(() => this.progressBar.stop())
    );
  }

  getIdsByFilter(includedIds: string[], excludedIds: string[], filterProvider: { filterState: any }): Observable<string[]> {
    if (includedIds.length) {
      return of(includedIds);
    }
    return this.client
      .post<string[]>(`${this.endpoint}/allIdsByFilter`, filterProvider.filterState)
      .pipe(map(response => response.filter(id => !excludedIds.find(excluded => excluded === id))));
  }
}
